const REL_SPLIT_PATTERN = /\s+/u;
const EXTERNAL_REL_TOKENS = ["noopener", "noreferrer"] as const;
const NOFOLLOW_TOKEN = "nofollow" as const;

export interface ComposeRelOptions {
  readonly rel?: string;
  readonly target?: string;
  readonly href?: string;
  readonly includeNofollowForExternal?: boolean;
}

const isExternalHref = (href: string | undefined): boolean => {
  const trimmed = href?.trim();
  if (!trimmed) {
    return false;
  }

  if (/^[/?#]/u.test(trimmed)) {
    return false;
  }

  const lower = trimmed.toLowerCase();

  if (lower.startsWith("mailto:")) {
    return false;
  }

  if (lower.startsWith("tel:")) {
    return false;
  }

  return (
    lower.startsWith("http://") ||
    lower.startsWith("https://") ||
    lower.startsWith("//")
  );
};

/**
 * Normalizes an anchor rel attribute, ensuring security tokens for new windows
 * and optionally enforcing `nofollow` for truly external destinations.
 */
export const composeRelAttribute = ({
  rel,
  target,
  href,
  includeNofollowForExternal = false,
}: ComposeRelOptions): string | undefined => {
  const tokens = new Set<string>();

  if (rel) {
    for (const token of rel.split(REL_SPLIT_PATTERN)) {
      if (token) {
        tokens.add(token);
      }
    }
  }

  if (target === "_blank") {
    for (const token of EXTERNAL_REL_TOKENS) {
      tokens.add(token);
    }

    if (includeNofollowForExternal && isExternalHref(href)) {
      tokens.add(NOFOLLOW_TOKEN);
    }
  }

  if (tokens.size === 0) {
    return undefined;
  }

  return Array.from(tokens).join(" ");
};
